import { useEffect, useRef, useState } from "react";
import type { StockItem } from "../../stock/model";
import type { StockAnswer } from "../../stock/sheet";
import Icon from "../components/Icon";
import { fmt, shopColor, weekNumber } from "./look";

// Changing what a shop requests (Viktor, 31 Oct 2026: "Here we need input box to change requested. Then store can go
// in and change this value. Put it above OK green field"). On a shop's page it is that shop's amount for the week;
// elsewhere one row per shop. A change is saved by itself a moment after the last tap, kept in this app's database and
// laid over the sheet's amount; the Google Sheet itself is not changed. Going back to the sheet's amount removes it.
// The order screen (main.soulcake.eu) has the same in its Inventory tab.

export type SaveRequest = (shop: string, amount: number) => Promise<void>;

const SAVE_AFTER_MS = 700;

function Row({ item, data, shop, week, save, showShop }: { item: StockItem; data: StockAnswer; shop: string; week: string; save: SaveRequest; showShop: boolean }) {
  const value = item.weekly[shop]?.[week] ?? 0;
  const notes = item.notes[shop]?.[week] ?? [];
  const changed = item.changed[shop]?.[week];
  const [draft, setDraft] = useState(String(value));
  const [state, setState] = useState<{ kind: "idle" | "saving" | "saved" } | { kind: "error"; message: string }>({ kind: "idle" });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => setDraft(String(value)), [value, item.id, week]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = async (amount: number) => {
    clearTimeout(timer.current);
    if (amount === value) return;
    setState({ kind: "saving" });
    try {
      await save(shop, amount);
      setState({ kind: "saved" });
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
      setDraft(String(value));
    }
  };
  const later = (amount: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void commit(amount), SAVE_AFTER_MS);
  };
  const step = (by: number) => {
    const next = Math.max(0, Math.min(999, (Number(draft) || 0) + by));
    setDraft(String(next));
    later(next);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {showShop && (
          <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] font-semibold">
            <span className="size-2 shrink-0 rounded-full" style={{ background: shopColor(data.shops, shop) }} />
            <span className="truncate">{shop}</span>
          </span>
        )}
        <div className="flex items-center rounded-full border border-line bg-white shadow-pill">
          <button type="button" onClick={() => step(-1)} disabled={Number(draft) <= 0} aria-label={`One less for ${shop}`} className="flex size-9 items-center justify-center text-rose-ink disabled:opacity-30">
            <Icon name="minus" size={16} />
          </button>
          <input
            inputMode="numeric"
            value={draft}
            aria-label={`Requested by ${shop}: ${item.name}`}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "").slice(0, 3);
              setDraft(digits);
              if (digits !== "") later(Number(digits));
            }}
            onBlur={() => draft !== "" && void commit(Number(draft))}
            onKeyDown={(e) => e.key === "Enter" && draft !== "" && void commit(Number(draft))}
            className="w-12 bg-transparent text-center text-[17px] font-bold tabular-nums outline-none"
          />
          <button type="button" onClick={() => step(1)} disabled={Number(draft) >= 999} aria-label={`One more for ${shop}`} className="flex size-9 items-center justify-center text-rose-ink disabled:opacity-30">
            <Icon name="plus" size={16} />
          </button>
        </div>
        {notes.length > 0 && <span className="text-[12px] text-ink-soft">+ {notes.join(" + ")}</span>}
        <span aria-live="polite" className={`text-[12px] ${state.kind === "error" ? "font-semibold text-danger" : "text-ink-faint"} ${showShop ? "w-16 text-right" : ""}`}>
          {state.kind === "saving" ? "Saving…" : state.kind === "saved" ? `✓ ${"Saved"}` : state.kind === "error" ? "Not saved" : ""}
        </span>
      </div>
      {state.kind === "error" && <div className="text-[12px] text-danger">{state.message}</div>}
      {changed && (
        <div className="flex flex-wrap items-center gap-x-2 text-[12px] text-ink-soft">
          <span title={new Date(changed.at).toLocaleString("en-GB", { timeZone: "Europe/Oslo" })}>
            {changed.by ? `Changed by ${changed.by} from ${fmt(changed.sheet)} in the sheet.` : `Changed here from ${fmt(changed.sheet)} in the sheet.`}
          </span>
          <button type="button" onClick={() => void commit(changed.sheet)} className="font-semibold text-rose-deep underline decoration-[#e0c6cc] underline-offset-2">
            {`Back to ${fmt(changed.sheet)}`}
          </button>
        </div>
      )}
    </div>
  );
}

export default function RequestEditor({ item, data, shop, week, save }: { item: StockItem; data: StockAnswer; shop: string | null; week: string; save: SaveRequest }) {
  const shops = shop ? [shop] : data.shops;
  return (
    <section className="rounded-2xl border border-line bg-white px-3.5 py-3">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">
        {shop ? `Requested · ${shop} · week ${weekNumber(week)}` : `Requested · week ${weekNumber(week)}`}
      </div>
      <div className="flex flex-col gap-2">
        {shops.map((s) => (
          <Row key={s} item={item} data={data} shop={s} week={week} save={save} showShop={!shop} />
        ))}
      </div>
      <p className="mt-2 text-[11.5px] leading-snug text-ink-faint">{"Kept in the app and counted everywhere here; the Google Sheet itself is not changed."}</p>
    </section>
  );
}
